import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dataTransformationService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import {
  Trash2,
  Check,
  RefreshCw
} from 'lucide-react';

function DataTransformation() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const { notify } = useNotification();

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const data = await dataTransformationService.getJobs();
      
      // Ensure data is an array before setting it
      if (data && data.jobs && Array.isArray(data.jobs)) {
        setJobs(data.jobs);
        console.log('Successfully loaded transformation jobs:', data.jobs.length);
      } else {
        console.error('Jobs data is not in expected format:', data);
        setJobs([]); // Set empty array as fallback
        
        if (data && typeof data === 'object' && !data.error) {
          notify.error('Invalid jobs data format received');
        }
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setJobs([]); // Ensure jobs is at least an empty array
      
      if (!error.message || !error.message.includes('auth')) {
        notify.error('Failed to load transformation jobs. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [notify]);

  // Filter jobs based on search term and status filter
  const filteredJobs = jobs.filter(job => {
    const transformationName = job.transformation?.name || '';
    const transformationQuery = job.transformation?.description || '';
    
    const matchesSearch = 
      transformationName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transformationQuery.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.job_id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      statusFilter === 'all' || 
      job.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Get clean status text
  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'generating':
        return 'Generating';
      case 'awaiting_approval':
        return 'Review Code';
      case 'approved':
        return 'Approved';
      case 'executing':
        return 'Executing';
      case 'pending':
        return 'Pending';
      case 'validating':
        return 'Validating';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  };

  // Get status styling classes
  const getStatusClasses = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400';
      case 'failed':
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400';
      case 'generating':
      case 'validating':
      case 'executing':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-400';
      case 'awaiting_approval':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-400';
      case 'approved':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400';
      default:
        return 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300';
    }
  };

  // Get display name with fallbacks
  const getDisplayName = (job) => {
    return job.transformation?.name || job.transformation?.description?.substring(0, 50) + '...' || `Transformation ${job.job_id.slice(0, 8)}`;
  };

  // Handle job deletion (cancel)
  const handleDeleteJob = async (jobId) => {
    try {
      await dataTransformationService.deleteJob(jobId);
      
      // Remove from state
      setJobs(prev => prev.filter(job => job.job_id !== jobId));
      setShowDeleteConfirm(null);
      notify.success('Job deleted successfully');
      
    } catch (error) {
      console.error('Delete job error:', error);
      notify.error('Failed to cancel job');
    }
  };

  // Render empty state
  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
        {searchTerm || statusFilter !== 'all' ? 'No transformations found' : 'No transformations yet'}
      </h3>
      <p className="text-slate-600 dark:text-slate-400 text-center max-w-md mb-8">
        {searchTerm || statusFilter !== 'all' 
          ? 'Try adjusting your search terms or filters.' 
          : 'Create your first transformation to get started.'}
      </p>
      {!searchTerm && statusFilter === 'all' && (
        <Link
          to="/data-transformation/create"
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-slate-800 font-medium transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
        >
          Create Transformation
        </Link>
      )}
    </div>
  );

  // List view for transformations
  const renderListView = () => (
    <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg">
      <div className="space-y-0">
        {filteredJobs.map((job, index) => {
          if (!job || !job.job_id) return null;
          
          const displayName = getDisplayName(job);
          
          return (
            <Link
              key={job.job_id || `job-${index}`}
              to={`/data-transformation/${job.job_id}`}
              className="relative flex items-center justify-between p-4 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-all duration-200 group border-b border-slate-200 dark:border-slate-700 last:border-b-0 cursor-pointer transform hover:scale-[1.003] hover:shadow-md hover:-translate-y-0.5"
            >
              <div className="flex items-center justify-between flex-1 min-w-0">
                {/* Name */}
                <div className="flex-1 min-w-0 pr-4">
                  <h3 className="font-medium text-slate-900 dark:text-white truncate">
                    {displayName}
                  </h3>
                </div>
                
                {/* Created Date */}
                <div className="text-sm text-slate-500 dark:text-slate-400 w-32 text-right">
                  {job.created_at ? new Date(job.created_at).toLocaleDateString() : 'Unknown'}
                </div>
                
                {/* Status */}
                <div className="w-28 flex items-center justify-end">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusClasses(job.status)}`}
                  >
                    {getStatusText(job.status)}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center ml-6">
                <button
                  type="button"
                  className={`opacity-0 group-hover:opacity-100 transition-all duration-200 p-1 transform hover:scale-110 ${
                    showDeleteConfirm === job.job_id
                      ? 'text-red-500 hover:text-red-600'
                      : 'text-slate-400 hover:text-red-500'
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (showDeleteConfirm === job.job_id) {
                      // Second click - actually delete
                      handleDeleteJob(job.job_id);
                    } else {
                      // First click - show confirm state
                      setShowDeleteConfirm(job.job_id);
                      // Auto-reset after 3 seconds if not clicked
                      setTimeout(() => {
                        setShowDeleteConfirm(null);
                      }, 3000);
                    }
                  }}
                  title={showDeleteConfirm === job.job_id ? "Click again to confirm deletion" : "Delete job"}
                >
                  {showDeleteConfirm === job.job_id ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 dark:text-white">Data Transformations</h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">
              Transform your datasets using natural language
            </p>
          </div>
          <Link
            to="/data-transformation/create"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-slate-800 font-medium transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
          >
            Create Transformation
          </Link>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 flex gap-3 items-center">
        <div className="flex-1">
          <input
            type="text"
            className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 dark:focus:ring-slate-600 focus:border-transparent"
            placeholder="Search transformations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="p-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg transform hover:scale-110 transition-all duration-200"
          onClick={() => fetchJobs()}
          disabled={loading}
          title="Refresh transformations"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-slate-300 dark:focus:ring-slate-600 focus:border-transparent"
        >
          <option value="all">All Status</option>
          <option value="completed">Completed</option>
          <option value="awaiting_approval">Review Code</option>
          <option value="generating">Generating</option>
          <option value="executing">Executing</option>
          <option value="failed">Failed</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Jobs content */}
      {loading ? (
        <div className="py-12 text-center">
          <div className="animate-spin h-6 w-6 border-2 border-slate-300 dark:border-slate-600 border-t-slate-600 dark:border-t-slate-300 rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Loading...</p>
        </div>
      ) : filteredJobs.length > 0 ? (
        renderListView()
      ) : (
        renderEmptyState()
      )}
    </div>
  );
}

export default DataTransformation;
